import { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Trash2, ArrowUp, ArrowDown, AlertTriangle, Wrench } from 'lucide-react';
import { Badge, Button, Input, Select } from '@dreamtree-org/twreact-ui';
import {
  METHODS, emptyCase, emptyRoute, addRoute, updateRoute, removeRoute, moveRoute,
  addCase, updateCase, removeCase, hasCatchAll, describeMatch, parseMatch, isJsonish,
} from '@/lib/mockApiSpec';

/**
 * The routes + cases editor — the part of the screen that IS the product: a route is a
 * method and a path, and each case under it is one input → output pair.
 *
 * Deliberately a list, not a canvas. Order is semantic here (the first case whose
 * matcher passes wins), and a list makes order visible and draggable-by-buttons in a
 * way a graph would obscure.
 *
 * Nothing here validates: the screen round-trips through POST /api/mock-api/validate,
 * which runs the same services/mockapi/authoring.js the save path does, so a second
 * copy of the rules would only be a source of disagreement. The two inline warnings
 * below (no catch-all, body is not JSON) are affordances, not verdicts.
 */
const textareaClass = 'w-full border rounded-md px-2 py-1.5 text-sm font-mono bg-[var(--app-surface)]';
const cardClass = 'rounded-lg border border-[var(--app-border)] bg-[var(--app-surface)]';

/** Is this case's body consistent with what it says it is? An affordance, not a
 *  verdict — the server refuses the save either way. */
function bodyParses(kase) {
  if (kase.bodyMode !== 'static') return true;
  if (!/json/i.test(kase.contentType || '')) return true;
  return isJsonish(kase.body);
}

function CaseSummary({ kase }) {
  return (
    <>
      <Badge variant={kase.status >= 400 ? 'error' : 'success'} size="sm">{kase.status}</Badge>
      {kase.scenario && kase.scenario !== 'default'
        ? <Badge variant="warning" size="sm">{kase.scenario}</Badge>
        : <span className="text-xs text-secondary-500">default</span>}
      <span className="text-xs text-secondary-500 truncate flex-1">
        {kase.name ? `${kase.name} — ` : ''}{describeMatch(kase.match)}
      </span>
      {!bodyParses(kase) && (
        <AlertTriangle size={14} className="text-amber-600" title="Body is declared as JSON but does not parse" />
      )}
    </>
  );
}

function CaseBody({ kase, set, readOnly, onRemove }) {
  const bodyOk = bodyParses(kase);
  return (
    <div className="px-3 pb-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input label="Case name" value={kase.name || ''} disabled={readOnly} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. card declined" />
            <Input
              label="Scenario"
              value={kase.scenario || 'default'}
              disabled={readOnly}
              onChange={(e) => set({ scenario: e.target.value })}
              placeholder="default"
            />
            <Input label="Status" type="number" value={kase.status} disabled={readOnly} onChange={(e) => set({ status: Number(e.target.value) || 200 })} />
          </div>

          <label className="block space-y-1">
            <span className="text-sm">Match (the INPUT)</span>
            <textarea
              className={textareaClass}
              rows={3}
              disabled={readOnly}
              defaultValue={JSON.stringify(kase.match || {}, null, 2)}
              onBlur={(e) => set({ match: parseMatch(e.target.value, kase.match || {}) })}
              placeholder='{"query":{"status":"active"}} — leave as {} to match every request'
            />
            <span className="block text-xs text-secondary-500">
              Supported: query, headers, params, jsonPath [&#123;path, equals&#125;], bodyContains.
              An empty matcher is the catch-all, so it belongs last.
            </span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Select
              label="Body mode"
              options={[{ label: 'Static', value: 'static' }, { label: 'Template', value: 'template' }]}
              value={kase.bodyMode || 'static'}
              disabled={readOnly}
              onChange={(v) => set({ bodyMode: v })}
            />
            <Input label="Content type" value={kase.contentType || 'application/json'} disabled={readOnly} onChange={(e) => set({ contentType: e.target.value })} />
            <Input label="Delay (ms)" type="number" value={kase.delayMs || 0} disabled={readOnly} onChange={(e) => set({ delayMs: Number(e.target.value) || 0 })} />
          </div>

          <label className="block space-y-1">
            <span className="text-sm">Body (the OUTPUT)</span>
            <textarea
              className={textareaClass}
              rows={7}
              disabled={readOnly}
              value={kase.body || ''}
              onChange={(e) => set({ body: e.target.value })}
            />
            {!bodyOk && (
              <span className="block text-xs text-amber-700">
                Declared as {kase.contentType} but this does not parse as JSON. Saving will be refused —
                set the content type to text/plain if that is intended.
              </span>
            )}
            {kase.bodyMode === 'template' && (
              <span className="block text-xs text-secondary-500">
                Tokens: &#123;&#123;params.id&#125;&#125; &#123;&#123;query.x&#125;&#125; &#123;&#123;headers.x&#125;&#125; &#123;&#123;body.a.b&#125;&#125; &#123;&#123;body&#125;&#125;
                &#123;&#123;uuid&#125;&#125; &#123;&#123;now.iso&#125;&#125; &#123;&#123;randomInt(1,100)&#125;&#125; &#123;&#123;faker.email&#125;&#125;.
                Anything else is returned literally.
              </span>
            )}
          </label>

          {!readOnly && (
            <div className="flex justify-end">
              <Button size="sm" variant="secondary" onClick={onRemove}>
                <Trash2 size={13} /> Remove case
              </Button>
            </div>
          )}
        </div>
  );
}

function CaseRow({ kase, routeIndex, caseIndex, spec, onChange, readOnly }) {
  const [open, setOpen] = useState(false);
  const set = (patch) => onChange(updateCase(spec, routeIndex, caseIndex, patch));

  return (
    <div className="border-t border-[var(--app-border)]">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[var(--app-border)]/20"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <CaseSummary kase={kase} />
      </button>
      {open && (
        <CaseBody
          kase={kase}
          set={set}
          readOnly={readOnly}
          onRemove={() => onChange(removeCase(spec, routeIndex, caseIndex))}
        />
      )}
    </div>
  );
}

function RouteCard({ route, index, spec, onChange, readOnly, tool }) {
  const [open, setOpen] = useState(true);
  const set = (patch) => onChange(updateRoute(spec, index, patch));

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="p-0.5">
          {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        <div className="w-28 shrink-0">
          <Select
            options={METHODS.map((m) => ({ label: m, value: m }))}
            value={route.method}
            disabled={readOnly}
            onChange={(v) => set({ method: v })}
          />
        </div>
        <div className="flex-1">
          <Input value={route.path} disabled={readOnly} onChange={(e) => set({ path: e.target.value })} placeholder="/v1/charges/:id" />
        </div>
        <span className="text-xs text-secondary-500 whitespace-nowrap">{(route.cases || []).length} case{(route.cases || []).length === 1 ? '' : 's'}</span>
        {!readOnly && (
          <>
            <Button size="sm" variant="ghost" title="Move up" onClick={() => onChange(moveRoute(spec, index, index - 1))}><ArrowUp size={13} /></Button>
            <Button size="sm" variant="ghost" title="Move down" onClick={() => onChange(moveRoute(spec, index, index + 1))}><ArrowDown size={13} /></Button>
            <Button size="sm" variant="ghost" title="Remove route" onClick={() => onChange(removeRoute(spec, index))}><Trash2 size={13} /></Button>
          </>
        )}
      </div>

      {open && (
        <>
          {!hasCatchAll(route) && (
            <div className="px-3 pb-2 text-xs text-amber-700 flex items-start gap-1">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>
                Every case here has a matcher, so a request that reaches this route but matches none of
                them falls through to the environment fallback. Add a case with an empty matcher last if
                that is not what you want.
              </span>
            </div>
          )}
          {(route.cases || []).map((kase, ci) => (
            <CaseRow
              key={`${index}-${ci}`}
              kase={kase}
              routeIndex={index}
              caseIndex={ci}
              spec={spec}
              onChange={onChange}
              readOnly={readOnly}
            />
          ))}
          <ToolRow tool={tool} route={route} index={index} spec={spec} onChange={onChange} readOnly={readOnly} />
          {!readOnly && (
            <div className="px-3 py-2 border-t border-[var(--app-border)]">
              <Button size="sm" variant="secondary" onClick={() => onChange(addCase(spec, index, emptyCase()))}>
                <Plus size={13} /> Add case
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * What an agent would call this route by. The name and classification come from the
 * SERVER (routes/mockApiRouter.js's toolSummary), never re-derived here — a second copy
 * of services/mockapi/toolDerive.js in the client is exactly the drift that would let
 * the screen promise a name the belt does not bind.
 */
function ToolRow({ tool, route, index, spec, onChange, readOnly }) {
  if (!tool) return null;
  const exposed = route.toolEnabled !== false;
  return (
    <div className="px-3 pb-2 flex flex-wrap items-center gap-2 text-xs border-t border-[var(--app-border)] pt-2">
      <Wrench size={13} className="text-secondary-500" />
      {exposed ? (
        <>
          <code className="font-mono">{tool.bound_name || tool.name}</code>
          <Badge variant={tool.category === 'auto' ? 'success' : 'warning'} size="sm">
            {tool.category}{tool.destructive ? ' · destructive' : ''}
          </Badge>
          {tool.params.length > 0 && (
            <span className="text-secondary-500">
              ({tool.params.map((p) => `${p.name}${p.optional ? '?' : ''}`).join(', ')})
            </span>
          )}
          {!tool.catalogued && <span className="text-amber-700">save to publish this tool</span>}
          {tool.catalogued && !tool.enabled && <span className="text-secondary-500">disabled in Tool Providers</span>}
        </>
      ) : (
        <span className="text-secondary-500">not offered as a tool</span>
      )}
      {!readOnly && (
        <label className="flex items-center gap-1 ml-auto">
          <input
            type="checkbox"
            checked={exposed}
            onChange={(e) => onChange(updateRoute(spec, index, { toolEnabled: e.target.checked }))}
          />
          Expose as a tool
        </label>
      )}
    </div>
  );
}

export default function RouteEditor({ spec, onChange, readOnly = false, tools = [] }) {
  const routes = spec.routes || [];
  // Positional: toolSummary is built from the same ordered route list the spec was read
  // from, so index is the only key that survives an unsaved reorder.
  const toolAt = (i) => tools[i] || null;
  return (
    <div className="space-y-3">
      {routes.length === 0 && (
        <div className="text-sm text-secondary-500 rounded-lg border border-dashed border-[var(--app-border)] p-6 text-center">
          No routes yet. Add one, or ask Mock API Builder to draft the whole thing from a description
          or a sample response.
        </div>
      )}
      {routes.map((route, i) => (
        <RouteCard key={i} route={route} index={i} spec={spec} onChange={onChange} readOnly={readOnly} tool={toolAt(i)} />
      ))}
      {!readOnly && (
        <Button size="sm" variant="secondary" onClick={() => onChange(addRoute(spec, emptyRoute()))}>
          <Plus size={14} /> Add route
        </Button>
      )}
    </div>
  );
}
